import {
	Button,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Input,
	Label,
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	Textarea,
} from "@monbits/ui";
import { AlertCircle, Loader2, Wallet } from "lucide-react";
import * as React from "react";
import { toast } from "sonner";
import { CryptoIcon } from "src/components/crypto-icon/crypto-icon";
import poolWithdrawalProvider from "src/providers/pool-withdrawal-provider";
import { formatCryptoAmount } from "src/utils/format";

interface Balance {
	amount: string;
	currency: string;
	symbol: string;
	usdAmount: string;
	blockchain: string;
}

interface CreatePoolWithdrawalDialogProps {
	isOpen: boolean;
	onClose: () => void;
	onSuccess: () => void;
	walletId: number;
	walletAddress: string;
	blockchain: string;
	balances: Balance[];
}

const CreatePoolWithdrawalDialog: React.FC<CreatePoolWithdrawalDialogProps> = ({
	isOpen,
	onClose,
	onSuccess,
	walletId,
	walletAddress,
	blockchain,
	balances,
}) => {
	const [destinationAddress, setDestinationAddress] = React.useState("");
	const [selectedCurrency, setSelectedCurrency] = React.useState("");
	const [amount, setAmount] = React.useState("");
	const [purpose, setPurpose] = React.useState("");
	const [totpCode, setTotpCode] = React.useState("");
	const [isSubmitting, setIsSubmitting] = React.useState(false);

	const selectedBalance = balances.find((b) => b.currency === selectedCurrency);

	const handleClose = React.useCallback(() => {
		if (isSubmitting) return;
		setDestinationAddress("");
		setSelectedCurrency("");
		setAmount("");
		setPurpose("");
		setTotpCode("");
		onClose();
	}, [isSubmitting, onClose]);

	const handleSetMax = () => {
		if (selectedBalance) {
			setAmount(selectedBalance.amount);
		}
	};

	const handleSubmit = React.useCallback(
		async (e: React.FormEvent) => {
			e.preventDefault();

			if (destinationAddress.trim().length < 10) {
				toast.error("Invalid destination address");
				return;
			}

			const numAmount = Number.parseFloat(amount);
			if (Number.isNaN(numAmount) || numAmount <= 0) {
				toast.error("Invalid amount");
				return;
			}

			if (purpose.trim().length < 10) {
				toast.error("Purpose must be at least 10 characters");
				return;
			}

			if (purpose.trim().length > 500) {
				toast.error("Purpose must be under 500 characters");
				return;
			}

			if (totpCode.trim().length !== 6) {
				toast.error("Enter a valid 6-digit 2FA code");
				return;
			}

			try {
				setIsSubmitting(true);

				await poolWithdrawalProvider.createWithdrawal({
					outboundWalletId: walletId,
					recipientAddress: destinationAddress.trim(),
					currency: selectedCurrency,
					amount: amount.trim(),
					purpose: purpose.trim(),
					totpCode: totpCode.trim(),
				});

				toast.success("Withdrawal created successfully");
				onSuccess();
				handleClose();
			} catch (error: unknown) {
				const errorMessage =
					(error as { response?: { data?: { message?: string } } })?.response
						?.data?.message || "Failed to create withdrawal";
				toast.error(errorMessage);
			} finally {
				setIsSubmitting(false);
			}
		},
		[
			walletId,
			destinationAddress,
			selectedCurrency,
			amount,
			purpose,
			totpCode,
			onSuccess,
			handleClose,
		],
	);

	// Calculate USD value for entered amount
	const estimatedUsd = React.useMemo(() => {
		if (!selectedBalance || !amount) return null;
		const numAmount = Number.parseFloat(amount);
		const balanceAmount = Number.parseFloat(selectedBalance.amount);
		const balanceUsd = Number.parseFloat(selectedBalance.usdAmount);
		if (Number.isNaN(numAmount) || balanceAmount === 0) return null;
		return (numAmount / balanceAmount) * balanceUsd;
	}, [selectedBalance, amount]);

	return (
		<Dialog open={isOpen} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[520px]">
				<form onSubmit={handleSubmit}>
					<DialogHeader>
						<DialogTitle className="flex items-center gap-2">
							<Wallet className="h-5 w-5" />
							Pool Withdrawal
						</DialogTitle>
						<DialogDescription>
							Send funds from {blockchain} wallet to an external address
						</DialogDescription>
					</DialogHeader>

					<div className="space-y-4 py-4">
						{/* Source Wallet Card */}
						<div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
							<CryptoIcon ticker={blockchain} className="h-8 w-8" />
							<div className="flex-1 min-w-0">
								<p className="text-xs text-muted-foreground">From</p>
								<p className="font-medium">{blockchain} Wallet</p>
								<p className="text-xs font-mono text-muted-foreground truncate">
									{walletAddress}
								</p>
							</div>
						</div>

						{/* Asset and Amount - Side by Side */}
						<div className="grid grid-cols-2 gap-3">
							{/* Asset Selection */}
							<div className="space-y-2">
								<Label>Asset</Label>
								<Select
									value={selectedCurrency}
									onValueChange={setSelectedCurrency}
									disabled={isSubmitting || balances.length === 0}
								>
									<SelectTrigger className="w-full">
										{selectedBalance ? (
											<div className="flex items-center gap-2">
												<CryptoIcon
													ticker={selectedBalance.symbol}
													className="h-4 w-4"
												/>
												<span>{selectedBalance.symbol}</span>
											</div>
										) : (
											<span className="text-muted-foreground">
												{balances.length === 0 ? "No assets" : "Select"}
											</span>
										)}
									</SelectTrigger>
									<SelectContent>
										{balances.length === 0 ? (
											<div className="px-2 py-4 text-sm text-muted-foreground text-center">
												No assets available in this wallet
											</div>
										) : (
											balances.map((balance) => (
												<SelectItem
													key={balance.currency}
													value={balance.currency}
												>
													<span className="flex items-center gap-2">
														<CryptoIcon
															ticker={balance.symbol}
															className="h-4 w-4"
														/>
														{balance.symbol} —{" "}
														{formatCryptoAmount(balance.amount, {
															maxDecimals: 4,
														})}
													</span>
												</SelectItem>
											))
										)}
									</SelectContent>
								</Select>
							</div>

							{/* Amount */}
							<div className="space-y-2">
								<div className="flex justify-between items-center">
									<Label>Amount</Label>
									{selectedBalance && (
										<button
											type="button"
											onClick={handleSetMax}
											className="text-xs text-primary hover:underline"
											disabled={isSubmitting}
										>
											Max
										</button>
									)}
								</div>
								<Input
									type="text"
									placeholder="0.00"
									value={amount}
									onChange={(e) => setAmount(e.target.value)}
									disabled={isSubmitting || !selectedCurrency}
									className="font-mono"
								/>
							</div>
						</div>

						{/* Balance Info - shown when asset is selected */}
						{selectedBalance && (
							<div className="flex justify-between items-center px-1 text-sm">
								<span className="text-muted-foreground">
									Available:{" "}
									{formatCryptoAmount(selectedBalance.amount, {
										maxDecimals: 8,
									})}{" "}
									{selectedBalance.symbol}
								</span>
								{estimatedUsd !== null && (
									<span className="text-muted-foreground">
										~$
										{formatCryptoAmount(estimatedUsd.toString(), {
											maxDecimals: 2,
										})}{" "}
										USD
									</span>
								)}
							</div>
						)}

						{/* Recipient Address */}
						<div className="space-y-2">
							<Label>Recipient Address</Label>
							<Input
								type="text"
								placeholder={`Enter ${blockchain} address`}
								value={destinationAddress}
								onChange={(e) => setDestinationAddress(e.target.value)}
								disabled={isSubmitting}
								className="font-mono text-sm"
							/>
						</div>

						{/* Purpose */}
						<div className="space-y-2">
							<div className="flex justify-between items-center">
								<Label>Purpose</Label>
								<span className="text-xs text-muted-foreground">
									{purpose.length}/500
								</span>
							</div>
							<Textarea
								placeholder="Why is this withdrawal needed? (min 10 characters)"
								value={purpose}
								onChange={(e) => setPurpose(e.target.value)}
								disabled={isSubmitting}
								className="min-h-[70px] resize-none text-sm"
								maxLength={500}
							/>
						</div>

						{/* 2FA Code */}
						<div className="space-y-2">
							<Label>2FA Code</Label>
							<Input
								type="text"
								placeholder="Enter 6-digit code"
								value={totpCode}
								onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
								disabled={isSubmitting}
								maxLength={6}
								inputMode="numeric"
								autoComplete="one-time-code"
								className="font-mono"
							/>
						</div>

						{/* Info notice */}
						<div className="flex gap-2 p-3 rounded-lg bg-muted/50 text-sm">
							<AlertCircle className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
							<p className="text-muted-foreground">
								Large withdrawals may require approval from another admin.
							</p>
						</div>
					</div>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={handleClose}
							disabled={isSubmitting}
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={
								isSubmitting ||
								!selectedCurrency ||
								!destinationAddress.trim() ||
								!amount.trim() ||
								purpose.trim().length < 10 ||
								totpCode.length !== 6
							}
						>
							{isSubmitting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Creating...
								</>
							) : (
								"Create Withdrawal"
							)}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
};

export default CreatePoolWithdrawalDialog;
